import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getClientStats } from '../../services/clients';
import { onQuerySuccess } from '../../utils/queryResponse';
import { intlPhoneDigits, openViberChat, rsdFormat, toLocalDateString } from '../../utils/helper';
import Avatar from '../../components/avatar';
import OverlayLoader from '../../components/overlayLoader';
import SbrDrawer from '../../components/sbr/SbrDrawer';
import ViberIcon from '../../components/icons/viberIcon';
import { getStatusLabel } from '../../constants/jobStatus';

/* ── friendly job-status labels ─────────────────────────────────────── */
// Anything not in this short-form list falls back to the canonical label
// from constants/jobStatus.js instead of raw enum text.
// Values are i18n keys, translated at render time via statusLabel(t, s).
const STATUS_LABEL_KEY = {
    SCHEDULED: 'jobStatus.SCHEDULED', ASSIGNED: 'clients.statusAssigned', PICKUP_IN_PROGRESS: 'clients.statusPickup',
    PICKUP_COMPLETED: 'clients.statusPickedUp', CLEANING: 'clients.statusCleaning', CLEANING_COMPLETED: 'clients.statusCleaned',
    DISPATCH_IN_PROGRESS: 'clients.statusOutForDelivery', COMPLETED: 'jobStatus.COMPLETED',
};
const statusLabel = (t, s) => (STATUS_LABEL_KEY[s] ? t(STATUS_LABEL_KEY[s]) : (s ? getStatusLabel(s) : '—'));

/* relative time: "today", "3 days ago", "6 months", "2 years" (ago = false drops the "ago") */
const relTime = (t, date, ago = true) => {
    if (!date) return '';
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    if (days < 0) return t('clients.relUpcoming');
    if (days === 0) return t('common.today');
    if (days === 1) return t('common.yesterday');
    if (days < 30) return ago ? t('clients.daysAgo', { count: days }) : t('clients.daysCount', { count: days });
    const months = Math.floor(days / 30);
    if (months < 12) return ago ? t('clients.monthsAgo', { count: months }) : t('clients.monthsCount', { count: months });
    const years = Math.floor(months / 12);
    return ago ? t('clients.yearsAgo', { count: years }) : t('clients.yearsCount', { count: years });
};
const shortDate = (d) => (d ? toLocalDateString(d, 'dd MMM yyyy') : '—');
const fmtPct = (v) => `${v >= 0 ? '↑' : '↓'} ${Math.abs(Number(v) || 0).toFixed(1).replace('.', ',')}%`;

/* ── icons ──────────────────────────────────────────────────────────── */
const UserIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
);
const IcMail = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg>;
const IcPhone = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>;
const IcPin = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
const IcWhats = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.94 11.94 0 0 0 5.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.86 11.86 0 0 0-3.481-8.408" /></svg>;
const IcViber = () => <ViberIcon />;
const IcDollar = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>;
const IcBrief = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>;
const IcCal = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>;
const IcClock = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
const IcDoc = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>;
const IcStar = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 3 1.9 4.6L18.5 9l-4.6 1.9L12 15l-1.9-4.1L5.5 9l4.6-1.4z" /></svg>;

const styles = `
  .cinfo{padding-bottom:6px}
  .cinfo-hero{display:flex;gap:14px;align-items:flex-start;margin-bottom:14px}
  .cinfo-hero .htx{min-width:0;flex:1}
  .cinfo-hero .nm{display:flex;align-items:center;gap:8px;font-size:18px;font-weight:800;color:var(--ink);letter-spacing:-.4px;flex-wrap:wrap}
  .cinfo-hero .badge{font-size:10px;font-weight:800;letter-spacing:.3px;color:var(--good);background:var(--good-bg);border:1px solid var(--good-bg);border-radius:999px;padding:2px 8px}
  .cinfo-hero .cl{display:flex;align-items:center;gap:7px;font-size:12.5px;color:var(--muted);font-weight:500;margin-top:6px;word-break:break-word}
  .cinfo-hero .cl svg{width:14px;height:14px;color:var(--faint);flex-shrink:0}
  .cinfo-qa{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap}
  .cinfo-qa .qa{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:700;color:var(--brand);background:var(--brand-50);border:1px solid var(--brand-100);border-radius:10px;padding:8px 12px;text-decoration:none;cursor:pointer}
  .cinfo-qa .qa svg{width:15px;height:15px}
  .cinfo-qa .qa.wa{color:#059669;background:#E7F8F1;border-color:#CDEFE0}
  .cinfo-qa .qa.vb{color:#7360F2;background:#EEEBFF;border-color:#DED8FB}
  .cinfo-kpis{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:16px}
  .cinfo-kpi{background:#fff;border:1px solid var(--line);border-radius:var(--r-md);padding:12px;box-shadow:var(--sh-1)}
  .cinfo-kpi .top{display:flex;align-items:center;gap:8px;margin-bottom:9px}
  .cinfo-kpi .ic{width:30px;height:30px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .cinfo-kpi .ic svg{width:15px;height:15px}
  .cinfo-kpi .lab{font-size:11px;font-weight:700;color:var(--muted);line-height:1.2}
  .cinfo-kpi .val{font-size:15.5px;font-weight:800;color:var(--ink);letter-spacing:-.4px;font-variant-numeric:tabular-nums}
  .cinfo-kpi .sub{font-size:11px;color:var(--muted);font-weight:600;margin-top:3px}
  .cinfo-kpi .sub .up{color:var(--good);font-weight:800}
  .cinfo-kpi .sub .down{color:var(--bad);font-weight:800}
  .cinfo-kpi .sub.bad{color:var(--bad);font-weight:800}
  .cinfo-kpi--link{cursor:pointer;transition:box-shadow .14s,border-color .14s,transform .06s}
  .cinfo-kpi--link:hover{border-color:var(--brand-100);box-shadow:0 4px 14px rgba(37,99,235,.16)}
  .cinfo-kpi--link:active{transform:translateY(1px)}
  .cinfo-kpi--link .lab-go{margin-left:auto;font-size:10.5px;font-weight:800;color:var(--brand);white-space:nowrap}
  .cinfo .ic-green{background:var(--done-bg);color:var(--done)} .cinfo .ic-blue{background:var(--pickup-bg);color:var(--pickup)}
  .cinfo .ic-purple{background:var(--cleaning-bg);color:var(--cleaning)} .cinfo .ic-amber{background:var(--delivery-bg);color:var(--delivery)}
  .cinfo .ic-rose{background:var(--bad-bg);color:var(--bad)} .cinfo .ic-star{background:#FEF6E7;color:#B45309}
  .cinfo-pill{display:inline-flex;align-items:center;font-size:10.5px;font-weight:800;padding:2px 8px;border-radius:7px;margin-top:5px;background:var(--pickup-bg);color:var(--pickup)}
  .cinfo-sec{background:#fff;border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--sh-1);padding:14px 16px;margin-bottom:14px}
  .cinfo-sec .hd{font-size:13px;font-weight:800;color:var(--ink);letter-spacing:-.2px;margin-bottom:12px}
  .cinfo-rows{display:grid;grid-template-columns:1fr 1fr;gap:12px 16px}
  .cinfo-r{min-width:0}
  .cinfo-r .k{font-size:10.5px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:var(--faint)}
  .cinfo-r .v{font-size:13px;font-weight:600;color:var(--ink);margin-top:3px;word-break:break-word}
  .cinfo-note{font-size:13px;color:var(--ink);font-weight:500;line-height:1.5;white-space:pre-wrap}
`;

const ClientInfoModal = ({ display, toggle, clientId, onViewJobs }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);

    const { data, isFetching } = useQuery(
        ['client-stats', clientId],
        () => clientId && getClientStats(clientId),
        { enabled: !!clientId }
    );

    useEffect(() => { onQuerySuccess(data, (result) => setStats(result)); }, [data]);

    const onHide = () => toggle(false);

    // Jump to the Jobs page filtered to this client. Also lets the parent
    // drawer (e.g. the create-job drawer) close itself first via onViewJobs.
    const goToClientJobs = () => {
        onHide();
        onViewJobs && onViewJobs();
        navigate(`/jobs?clientId=${clientId}&clientName=${encodeURIComponent(fullName)}`);
    };

    const c = stats?.client;
    const isCompany = c?.clientType === 'COMPANY';
    const contactName = c ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() : '';
    const fullName = isCompany ? (c?.companyName || contactName) : contactName;
    const phoneDigits = intlPhoneDigits(c?.phone);
    const address = c ? [c.street, c.city, c.state, c.zipCode, c.country].filter(Boolean).join(', ') : '';

    return (
        <SbrDrawer
            show={display}
            onHide={onHide}
            secondary
            title={t('clients.clientInformation')}
            subtitle={t('clients.overviewAndDetails')}
            icon={<UserIcon />}
            footer={<button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide}>{t('common.close')}</button>}
        >
            <style>{styles}</style>

            {!c ? (
                <div style={{ position: 'relative', minHeight: 240 }}>{isFetching && <OverlayLoader />}</div>
            ) : (
                <div className="cinfo">
                    {/* hero */}
                    <div className="cinfo-hero">
                        <Avatar square size={64} name={fullName} src={c.avatarUrl} />
                        <div className="htx">
                            <div className="nm">{fullName || '—'}<span className="badge">{isCompany ? t('clients.company') : t('clients.individual')}</span><span className="badge">{(stats.totalJobs ?? 0) > 0 ? t('clients.active') : t('clients.new')}</span></div>
                            {isCompany && contactName && <div className="cl"><UserIcon />{t('clients.contact')}: {contactName}</div>}
                            {c.email && <div className="cl"><IcMail />{c.email}</div>}
                            {c.phone && <div className="cl"><IcPhone />{c.phone}</div>}
                            {address && <div className="cl"><IcPin />{address}</div>}
                        </div>
                    </div>

                    {/* quick actions */}
                    {phoneDigits && (
                        <div className="cinfo-qa">
                            <a className="qa" href={`tel:${c.phone}`}><IcPhone />{t('clients.call')}</a>
                            <a className="qa wa" href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noreferrer"><IcWhats />WhatsApp</a>
                            <a className="qa vb" href={`viber://chat/?number=%2B${phoneDigits}`} onClick={(e) => { e.preventDefault(); openViberChat(c.phone); }}><IcViber />Viber</a>
                        </div>
                    )}

                    {/* KPIs */}
                    <div className="cinfo-kpis">
                        <div className="cinfo-kpi">
                            <div className="top"><span className="ic ic-green"><IcDollar /></span><span className="lab">{t('clients.totalRevenue')}</span></div>
                            <div className="val">{rsdFormat(stats.totalRevenue)}</div>
                            <div className="sub"><span className={stats.revenueTrendPct >= 0 ? 'up' : 'down'}>{fmtPct(stats.revenueTrendPct)}</span> {t('clients.vsLast12Months')}</div>
                        </div>
                        <div
                            className="cinfo-kpi cinfo-kpi--link"
                            role="button"
                            tabIndex={0}
                            title={t('clients.viewAllJobs')}
                            onClick={goToClientJobs}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToClientJobs(); } }}
                        >
                            <div className="top"><span className="ic ic-blue"><IcBrief /></span><span className="lab">{t('clients.totalJobsLabel')}</span>{(stats.totalJobs ?? 0) > 0 && <span className="lab-go">{t('clients.viewAll')}</span>}</div>
                            <div className="val">{stats.totalJobs}</div>
                            <div className="sub"><span className={stats.jobsTrendDelta >= 0 ? 'up' : 'down'}>{stats.jobsTrendDelta >= 0 ? '↑' : '↓'} {Math.abs(stats.jobsTrendDelta)}</span> {t('clients.vsLast12Months')}</div>
                        </div>
                        <div className="cinfo-kpi">
                            <div className="top"><span className="ic ic-purple"><IcCal /></span><span className="lab">{t('clients.lastJob')}</span></div>
                            <div className="val">{stats.lastJob ? shortDate(stats.lastJob.date) : '—'}</div>
                            <div className="sub">{stats.lastJob ? relTime(t, stats.lastJob.date) : t('clients.noJobsYet')}</div>
                        </div>
                        <div className="cinfo-kpi">
                            <div className="top"><span className="ic ic-amber"><IcClock /></span><span className="lab">{t('clients.activeJob')}</span></div>
                            <div className="val">{stats.activeJob ? `#${stats.activeJob.id}` : '—'}</div>
                            {stats.activeJob ? <span className="cinfo-pill">{statusLabel(t, stats.activeJob.status)}</span> : <div className="sub">{t('clients.noneInProgress')}</div>}
                        </div>
                        <div className="cinfo-kpi">
                            <div className="top"><span className="ic ic-rose"><IcDoc /></span><span className="lab">{t('clients.unpaidInvoices')}</span></div>
                            <div className="val">{rsdFormat(stats.unpaidAmount)}</div>
                            <div className={`sub ${stats.unpaidCount > 0 ? 'bad' : ''}`}>{t('clients.invoiceCount', { count: stats.unpaidCount })}</div>
                        </div>
                        <div className="cinfo-kpi">
                            <div className="top"><span className="ic ic-star"><IcStar /></span><span className="lab">{t('clients.customerSince')}</span></div>
                            <div className="val">{shortDate(c.customerSince)}</div>
                            <div className="sub">{relTime(t, c.customerSince, false)}</div>
                        </div>
                    </div>

                    {/* company details */}
                    {isCompany && (
                        <div className="cinfo-sec">
                            <div className="hd">{t('clients.companyDetails')}</div>
                            <div className="cinfo-rows">
                                <div className="cinfo-r"><div className="k">{t('clients.companyNameLabel')}</div><div className="v">{c.companyName || '—'}</div></div>
                                <div className="cinfo-r"><div className="k">{t('clients.contactPerson')}</div><div className="v">{contactName || '—'}</div></div>
                                <div className="cinfo-r"><div className="k">{t('clients.registrationNo')}</div><div className="v">{c.registrationNumber || '—'}</div></div>
                                <div className="cinfo-r"><div className="k">{t('clients.taxVatNo')}</div><div className="v">{c.taxNumber || '—'}</div></div>
                            </div>
                        </div>
                    )}

                    {/* location & access */}
                    <div className="cinfo-sec">
                        <div className="hd">{t('clients.locationAndAccess')}</div>
                        <div className="cinfo-rows">
                            <div className="cinfo-r"><div className="k">{t('clients.street')}</div><div className="v">{c.street || '—'}</div></div>
                            <div className="cinfo-r"><div className="k">{t('clients.apartmentNo')}</div><div className="v">{c.apartmentNo || '—'}</div></div>
                            <div className="cinfo-r"><div className="k">{t('clients.floor')}</div><div className="v">{c.floor || '—'}</div></div>
                            <div className="cinfo-r"><div className="k">{t('clients.intercomName')}</div><div className="v">{c.intercomName || '—'}</div></div>
                            <div className="cinfo-r"><div className="k">{t('clients.city')}</div><div className="v">{c.city || '—'}</div></div>
                            <div className="cinfo-r"><div className="k">{t('clients.state')}</div><div className="v">{c.state || '—'}</div></div>
                            <div className="cinfo-r"><div className="k">{t('clients.zipCode')}</div><div className="v">{c.zipCode || '—'}</div></div>
                            <div className="cinfo-r"><div className="k">{t('clients.country')}</div><div className="v">{c.country || '—'}</div></div>
                        </div>
                    </div>

                    {/* notes */}
                    {c.notes && (
                        <div className="cinfo-sec">
                            <div className="hd">{t('clients.notes')}</div>
                            <div className="cinfo-note">{c.notes}</div>
                        </div>
                    )}

                    {isFetching && <OverlayLoader />}
                </div>
            )}
        </SbrDrawer>
    );
};

export default ClientInfoModal;
